import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { FileCheck, Plus, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function TitleReports() {
  const { API } = useAuth();
  const [reports, setReports] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ property_id: "", title: "", description: "" });

  const fetchData = useCallback(async () => {
    try {
      const [rRes, pRes] = await Promise.all([
        fetch(`${API}/title-reports`, { credentials: "include" }),
        fetch(`${API}/properties`, { credentials: "include" }),
      ]);
      if (rRes.ok) setReports(await rRes.json());
      if (pRes.ok) setProperties(await pRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    try {
      const res = await fetch(`${API}/title-reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) { setShowForm(false); setForm({ property_id: "", title: "", description: "" }); fetchData(); }
    } catch (e) { console.error(e); }
  };

  const getPropertyName = (id) => properties.find((p) => p.property_id === id)?.name || id;

  return (
    <div data-testid="title-reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Title Reports</h2>
          <p className="text-white/40 text-sm font-sans">{reports.length} reports requested</p>
        </div>
        <Button data-testid="request-title-report-btn" onClick={() => setShowForm(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
          <Plus className="w-4 h-4 mr-2" /> Request Report
        </Button>
      </div>

      {reports.length === 0 ? (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
          <FileCheck className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-sans">No title reports yet. Request one for your properties.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.report_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FileCheck className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                  <div>
                    <h4 className="font-sans font-medium text-white text-sm">{r.title}</h4>
                    <p className="text-white/40 text-xs font-sans mt-0.5">Property: {getPropertyName(r.property_id)}</p>
                    {r.description && <p className="text-white/50 text-xs font-sans mt-2">{r.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3 text-white/30" />
                  <span className="text-[#F59E0B] text-xs font-sans uppercase">{r.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Request Title Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Property</label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="report-property-select" className="bg-white/5 border-white/10 text-white rounded-sm">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  {properties.map((p) => (
                    <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Report Title</label>
              <Input data-testid="report-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Full Title Verification" className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Additional details..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-report-btn" onClick={handleSubmit} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">Submit Request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
